import { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Upload, Paperclip, X, HandCoins, CheckCircle, XCircle, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ArchiveButton } from "@/components/ArchiveButton";
import { StatusBadge } from "@/components/StatusBadge";
import { PreviewButton } from "@/components/PreviewButton";

const EMPTY = {
  budget_request_id: "",
  title: "",
  department: "",
  amount_released: 0,
  items: [{ description: "", amount: 0, receipt_ref: "" }],
  report_text: "",
  attachments: [],
  period_start: "",
  period_end: "",
};

function authedHref(url) {
  if (!url) return "#";
  if (url.startsWith("/api/files/")) {
    const t = localStorage.getItem("access_token") || "";
    return `${process.env.REACT_APP_BACKEND_URL}${url}?auth=${encodeURIComponent(t)}`;
  }
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

const STATUS_TONE = {
  submitted: "bg-blue-50 text-blue-800 border-blue-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-50 text-rose-800 border-rose-200",
  draft: "bg-muted text-foreground border-border",
};

export default function LiquidationsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewNote, setReviewNote] = useState("");

  async function load() {
    const [l, b] = await Promise.all([api.get("/liquidations"), api.get("/budget-requests")]);
    setItems(l.data); setBudgets(b.data);
  }
  useEffect(() => { load(); }, []);

  const canReview = can(user, ["finance"]);
  const canSubmit = can(user, ["department", "finance", "procurement"]);

  const totalSpent = useMemo(
    () => (form.items || []).reduce((s, i) => s + (Number(i.amount) || 0), 0),
    [form.items]
  );
  const variance = (Number(form.amount_released) || 0) - totalSpent;

  function openCreate() {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  }
  function openEdit(row) {
    setEditing(row);
    setForm({
      budget_request_id: row.budget_request_id,
      title: row.title || "",
      department: row.department || "",
      amount_released: row.amount_released || 0,
      items: (row.items && row.items.length) ? row.items : [{ description: "", amount: 0, receipt_ref: "" }],
      report_text: row.report_text || "",
      attachments: row.attachments || [],
      period_start: row.period_start || "",
      period_end: row.period_end || "",
    });
    setOpen(true);
  }
  function onBudgetPick(bid) {
    const b = budgets.find((x) => x.id === bid);
    if (!b) return;
    setForm((f) => ({ ...f, budget_request_id: bid, title: b.title, department: b.department, amount_released: b.total_amount || 0 }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.budget_request_id) return toast.error("Pick the released budget you're liquidating.");
    try {
      if (editing) await api.put(`/liquidations/${editing.id}`, form);
      else await api.post("/liquidations", form);
      toast.success(editing ? "Liquidation updated" : "Liquidation submitted");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  }

  async function uploadFiles(files) {
    setUploading(true);
    const uploaded = [];
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const token = localStorage.getItem("access_token");
        const res = await fetch(`${API_BASE}/uploads/billing`, {
          method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("upload failed");
        uploaded.push(await res.json());
      }
      setForm((f) => ({ ...f, attachments: [...(f.attachments || []), ...uploaded] }));
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  }

  async function submitReview(status) {
    if (!reviewTarget) return;
    try {
      await api.post(`/liquidations/${reviewTarget.id}/review`, { status, note: reviewNote || null });
      toast.success(status === "approved" ? "Liquidation approved" : "Sent back for revision");
      setReviewTarget(null); setReviewNote(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  }

  const releasedBudgets = budgets.filter((b) => b.status === "in_progress" || b.status === "completed");

  return (
    <div className="space-y-6" data-testid="liquidations-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2 flex items-center gap-1.5"><HandCoins size={12} /> Post-release</div>
          <h1 className="section-title text-3xl sm:text-4xl">Liquidations</h1>
          <p className="text-sm text-muted-foreground mt-1">Submit operational reports and receipts against a released budget — Finance reviews the variance.</p>
        </div>
        {canSubmit && <Button onClick={openCreate} data-testid="new-liq-btn"><Plus size={16} className="mr-2" /> New liquidation</Button>}
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table className="min-w-[1200px]">
          <TableHeader>
            <TableRow>
              <TableHead>No.</TableHead>
              <TableHead>Budget</TableHead>
              <TableHead>Dept.</TableHead>
              <TableHead className="text-right">Released</TableHead>
              <TableHead className="text-right">Spent</TableHead>
              <TableHead className="text-right">Variance</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">No liquidations yet. Once a budget is released, submit an operational report here.</TableCell></TableRow>
            ) : items.map((row) => {
              const bud = budgets.find((b) => b.id === row.budget_request_id);
              const varn = row.variance || 0;
              return (
                <TableRow key={row.id} data-testid={`liq-row-${row.id}`}>
                  <TableCell className="font-mono text-xs">{row.liquidation_no}</TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium truncate max-w-[240px]">{row.title || "—"}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{bud?.request_no || row.budget_request_id?.slice(0, 8)}</div>
                  </TableCell>
                  <TableCell className="text-sm">{row.department || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">₱{(row.amount_released || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right tabular-nums">₱{(row.amount_spent || 0).toLocaleString()}</TableCell>
                  <TableCell className={`text-right tabular-nums ${varn < 0 ? "text-rose-700" : "text-emerald-700"}`}>₱{varn.toLocaleString()}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 items-start">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${STATUS_TONE[row.status] || STATUS_TONE.draft}`}>{row.status}</span>
                      {row.status === "approved" && row.variance_action === "added_to_cash" && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-800 border-emerald-200 whitespace-nowrap" title="Positive variance added to today's Cash Position" data-testid={`liq-badge-cash-${row.id}`}>
                          + Added to Cash
                        </span>
                      )}
                      {row.status === "approved" && row.variance_action === "refund_created" && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-amber-50 text-amber-900 border-amber-200 whitespace-nowrap" title={`Auto-generated refund voucher for the negative variance`} data-testid={`liq-badge-refund-${row.id}`}>
                          Refund {row.variance_ref_no}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{row.submitted_by_name}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <PreviewButton endpointPath={`/exports/liquidation/${row.id}`} baseName={`liquidation_${row.liquidation_no}`} title={`Liquidation ${row.liquidation_no}`} testId={`preview-liq-${row.id}`} />
                      {canReview && row.status === "submitted" && (
                        <Button size="sm" variant="outline" className="border-border" onClick={() => setReviewTarget(row)} data-testid={`review-liq-${row.id}`}>
                          Review
                        </Button>
                      )}
                      {(row.submitted_by === user?.id || user?.role === "admin") && row.status !== "approved" && (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(row)} data-testid={`edit-liq-${row.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                      )}
                      {(user?.role === "admin" || row.submitted_by === user?.id) && !row.deleted_at && (
                        <ArchiveButton endpoint={`/liquidations/${row.id}`} label={row.liquidation_no} onArchived={load} testId={`archive-liq-${row.id}`} />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-black">{editing ? `Edit ${editing.liquidation_no}` : "Submit liquidation"}</DialogTitle>
            <DialogDescription>Report how the released budget was spent. Attach receipts, invoices, or reports.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Released Budget</Label>
                <Select value={form.budget_request_id} onValueChange={onBudgetPick} disabled={!!editing}>
                  <SelectTrigger data-testid="liq-budget-select"><SelectValue placeholder="Pick the budget being liquidated" /></SelectTrigger>
                  <SelectContent>
                    {releasedBudgets.map((b) => <SelectItem key={b.id} value={b.id}>{b.request_no} — {b.title} — ₱{(b.total_amount || 0).toLocaleString()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Department</Label>
                <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="border-border" data-testid="liq-dept-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount Released (₱)</Label>
                <Input type="number" step="0.01" value={form.amount_released} onChange={(e) => setForm({ ...form, amount_released: Number(e.target.value) })} className="border-border tabular-nums" data-testid="liq-released-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Period Start</Label>
                <Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="border-border" data-testid="liq-period-start" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Period End</Label>
                <Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="border-border" data-testid="liq-period-end" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Spend items</Label>
                <button type="button" onClick={() => setForm((f) => ({ ...f, items: [...f.items, { description: "", amount: 0, receipt_ref: "" }] }))} className="text-xs underline text-muted-foreground hover:text-foreground" data-testid="liq-add-item-btn">+ add row</button>
              </div>
              <div className="border border-border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="text-left px-3 py-2">Description</th>
                      <th className="text-left px-3 py-2 w-32">Receipt Ref.</th>
                      <th className="text-right px-3 py-2 w-32">Amount (₱)</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-2 py-1"><Input value={it.description} onChange={(e) => {
                          const items = [...form.items]; items[i] = { ...it, description: e.target.value }; setForm({ ...form, items });
                        }} className="border-transparent bg-transparent" data-testid={`liq-item-desc-${i}`} /></td>
                        <td className="px-2 py-1"><Input value={it.receipt_ref || ""} onChange={(e) => {
                          const items = [...form.items]; items[i] = { ...it, receipt_ref: e.target.value }; setForm({ ...form, items });
                        }} className="border-transparent bg-transparent" data-testid={`liq-item-ref-${i}`} /></td>
                        <td className="px-2 py-1"><Input type="number" step="0.01" value={it.amount} onChange={(e) => {
                          const items = [...form.items]; items[i] = { ...it, amount: Number(e.target.value) }; setForm({ ...form, items });
                        }} className="border-transparent bg-transparent text-right tabular-nums" data-testid={`liq-item-amount-${i}`} /></td>
                        <td className="px-1 py-1 text-center">
                          <button type="button" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }))} className="text-muted-foreground hover:text-rose-600" data-testid={`liq-item-remove-${i}`}><X size={14} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted/20 text-xs">
                    <tr>
                      <td className="px-3 py-2 uppercase tracking-widest text-muted-foreground" colSpan={2}>Total spent</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">₱{totalSpent.toLocaleString()}</td>
                      <td></td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 uppercase tracking-widest text-muted-foreground" colSpan={2}>Variance</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${variance < 0 ? "text-rose-700" : "text-emerald-700"}`}>₱{variance.toLocaleString()}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Operational Report / Narrative</Label>
              <Textarea rows={5} value={form.report_text} onChange={(e) => setForm({ ...form, report_text: e.target.value })} placeholder="Summary of activities, outcomes, deviations…" className="border-border" data-testid="liq-report-text" />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Attachments</Label>
              <label className="inline-flex items-center gap-2 text-xs border border-dashed border-border rounded px-3 py-2 hover:bg-muted cursor-pointer" data-testid="liq-upload-label">
                <Upload size={14} />
                {uploading ? "Uploading…" : "Attach receipts, invoices, reports"}
                <input type="file" multiple hidden onChange={(e) => e.target.files && uploadFiles(Array.from(e.target.files))} data-testid="liq-upload-input" />
              </label>
              {(form.attachments || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {form.attachments.map((a, i) => (
                    <div key={i} className="inline-flex items-center gap-1.5 text-xs border border-border rounded px-2 py-1 bg-muted/40" data-testid={`liq-attach-${i}`}>
                      <Paperclip size={12} />
                      <a href={authedHref(a.file_url)} target="_blank" rel="noreferrer" className="hover:underline">{a.file_name}</a>
                      <button type="button" onClick={() => setForm((f) => ({ ...f, attachments: f.attachments.filter((_, j) => j !== i) }))} className="text-muted-foreground hover:text-foreground">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="liq-submit-btn">{editing ? "Save changes" : "Submit for review"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!reviewTarget} onOpenChange={(v) => { if (!v) { setReviewTarget(null); setReviewNote(""); } }}>
        <DialogContent data-testid="review-liq-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black">Review liquidation</DialogTitle>
            <DialogDescription>
              {reviewTarget?.liquidation_no} — {reviewTarget?.title}. Released ₱{(reviewTarget?.amount_released || 0).toLocaleString()} vs Spent ₱{(reviewTarget?.amount_spent || 0).toLocaleString()}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {reviewTarget?.report_text && (
              <div className="text-sm border border-border rounded p-3 bg-muted/30 whitespace-pre-wrap">{reviewTarget.report_text}</div>
            )}
            {(reviewTarget?.attachments || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {reviewTarget.attachments.map((a, i) => (
                  <a key={i} href={authedHref(a.file_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs border border-border rounded px-2 py-1 hover:bg-muted">
                    <Paperclip size={12} />{a.file_name}
                  </a>
                ))}
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reviewer note (optional)</Label>
              <Textarea rows={2} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} className="border-border" data-testid="review-liq-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => submitReview("rejected")} data-testid="review-liq-reject"><XCircle size={14} className="mr-1" /> Send back</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => submitReview("approved")} data-testid="review-liq-approve"><CheckCircle size={14} className="mr-1" /> Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
